import React, { useState } from "react";
import { useDispatch } from "../redux/store";
import { AppointmentState } from "../types";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import { AppDispatch } from "../redux/store";

const DEFAULT_CHECKBOX_LABEL =
  "Interested in dropping your car off after-hours?";
const DEFAULT_DESCRIPTION =
  "Please review the resources below before dropping off your vehicle after-hours.";

interface AfterHoursDropOffCardProps {
  appointment: AppointmentState;
}

export const AfterHoursDropOffCard: React.FC<AfterHoursDropOffCardProps> = ({
  appointment,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const accentColor = appointment?.shopData?.color || "#2b73b9";
  const config = appointment?.shopData?.afterHoursDropOff;

  const checkboxLabel = config?.checkboxLabel?.trim()
    ? config.checkboxLabel
    : DEFAULT_CHECKBOX_LABEL;
  const description = config?.description?.trim()
    ? config.description
    : DEFAULT_DESCRIPTION;
  const resources = config?.resources || [];
  const termsOfService = config?.termsOfService || "";
  const isChecked = !!appointment?.afterHoursDropOffRequested;

  const [tosOpen, setTosOpen] = useState(false);

  const toggleChecked = () => {
    dispatch(
      updateAppointment({
        updates: { afterHoursDropOffRequested: !isChecked },
      })
    );
  };

  return (
    <div
      className="rounded-2xl bg-white p-5 font-roboto"
      style={{
        border: `2px solid ${accentColor}33`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
      data-testid="after-hours-drop-off-card"
    >
      <div
        className="flex items-center gap-3 cursor-pointer"
        onClick={toggleChecked}
        data-testid="after-hours-drop-off-checkbox"
      >
        <div
          className="flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0"
          style={{
            border: `2px solid ${accentColor}`,
            backgroundColor: isChecked ? accentColor : "#f3f4f6",
          }}
        >
          {isChecked && (
            <i
              className="fa-solid fa-check"
              aria-hidden="true"
              style={{ color: "#ffffff", fontSize: "14px", lineHeight: 1 }}
            />
          )}
        </div>
        <div
          className="text-sm font-semibold leading-snug"
          style={{ color: "#1f2937" }}
        >
          {checkboxLabel}
        </div>
      </div>

      {isChecked && (description || resources.length > 0 || termsOfService) && (
        <div className="mt-4 flex flex-col gap-2">
          {description && (
            <div
              className="text-sm"
              style={{ color: "#4b5563", whiteSpace: "pre-line" }}
              data-testid="after-hours-drop-off-description"
            >
              {description}
            </div>
          )}
          {resources.map((resource, idx) => (
            <a
              key={resource.id || `${resource.url}-${idx}`}
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold"
              style={{ color: accentColor }}
              data-testid="after-hours-drop-off-resource"
            >
              <i
                className="fa-solid fa-arrow-up-right-from-square"
                aria-hidden="true"
                style={{ fontSize: "12px" }}
              />
              <span>{resource.label || resource.url}</span>
            </a>
          ))}

          {termsOfService && (
            <button
              type="button"
              className="inline-flex items-center gap-2 text-sm font-semibold self-start"
              style={{
                color: accentColor,
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={() => setTosOpen(true)}
              data-testid="after-hours-drop-off-tos-trigger"
            >
              <i
                className="fa-solid fa-file-lines"
                aria-hidden="true"
                style={{ fontSize: "12px" }}
              />
              <span>View Terms of Service</span>
            </button>
          )}
        </div>
      )}

      {tosOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ backgroundColor: "rgba(17,24,39,0.6)" }}
          onClick={() => setTosOpen(false)}
          data-testid="after-hours-drop-off-tos-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden font-roboto"
            style={{
              boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-5 py-4"
              style={{
                backgroundColor: accentColor,
                color: "#ffffff",
              }}
            >
              <div className="flex items-center gap-2">
                <i
                  className="fa-solid fa-file-lines"
                  aria-hidden="true"
                  style={{ fontSize: "16px" }}
                />
                <h3 className="text-base font-bold m-0">Terms of Service</h3>
              </div>
              <button
                type="button"
                aria-label="Close"
                className="flex items-center justify-center w-8 h-8 rounded-full"
                style={{
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#ffffff",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={() => setTosOpen(false)}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>

            <div
              className="px-5 py-4 overflow-y-auto text-sm leading-relaxed"
              style={{ color: "#1f2937", whiteSpace: "pre-line" }}
            >
              {termsOfService}
            </div>

            <div
              className="px-5 py-3 flex justify-end"
              style={{ borderTop: "1px solid #e5e7eb" }}
            >
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-semibold"
                style={{
                  backgroundColor: accentColor,
                  color: "#ffffff",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={() => setTosOpen(false)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
